/**
 * Phase 3.43 — Werkstatt-Helpers extrahiert aus DealerProfileSection.
 * Wiederverwendbar in der neuen `WerkstattTeileZubehoerSection`.
 */
import { useEffect, useState } from "react";
import { Truck, ExternalLink, Phone, Mail, CheckCircle2 } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import api from "../../lib/api";
import { toast } from "sonner";

// Phase 3.37 — Konfigurierbare Werkstatt-Buchungsslots pro Wochentag.
export const DEFAULT_SLOTS = ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00"];
export const WEEKDAYS = [
    { key: "mon", label: "Montag" },
    { key: "tue", label: "Dienstag" },
    { key: "wed", label: "Mittwoch" },
    { key: "thu", label: "Donnerstag" },
    { key: "fri", label: "Freitag" },
    { key: "sat", label: "Samstag" },
    { key: "sun", label: "Sonntag" },
];
export const TIME_OPTIONS = (() => {
    const out = [];
    for (let h = 7; h <= 19; h++) {
        out.push(`${String(h).padStart(2, "0")}:00`);
        out.push(`${String(h).padStart(2, "0")}:30`);
    }
    return out;
})();

// Phase 3.38 — Standard-Services für Dauer-Konfiguration
export const DEFAULT_SERVICE_TYPES = [
    "Inspektion",
    "Ölwechsel",
    "Reifenwechsel",
    "Bremsen",
    "Beleuchtung",
    "Reifenmontage",
    "Reifenreparatur",
    "TÜV/HU",
    "Sonstiges",
];
export const DURATION_OPTIONS = [
    { v: 30, l: "30 min" },
    { v: 60, l: "60 min" },
    { v: 90, l: "90 min" },
    { v: 120, l: "2 h" },
    { v: 180, l: "3 h" },
    { v: 240, l: "4 h" },
];

export function ServiceDurationsEditor({ value, onChange }) {
    const durations = value || {};
    const setDuration = (svc, minutes) => {
        const next = { ...durations };
        if (minutes === 60 || minutes == null) {
            delete next[svc];
        } else {
            next[svc] = minutes;
        }
        onChange(Object.keys(next).length ? next : null);
    };
    return (
        <div className="mt-3 border-t border-gray-200 pt-3" data-testid="workshop-service-durations-editor">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 block mb-2">
                Service-spezifische Termin-Dauer
            </Label>
            <p className="text-[10px] text-gray-500 mb-2 leading-tight">
                Standard ist 60 min pro Termin. Hier können Sie die Dauer pro Service-Typ überschreiben (z.B. Inspektion 2h).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {DEFAULT_SERVICE_TYPES.map((svc) => {
                    const current = durations[svc] || 60;
                    return (
                        <div key={svc} className="flex items-center gap-2 bg-white border border-gray-200 rounded-sm p-2" data-testid={`workshop-service-duration-row-${svc}`}>
                            <span className="text-xs font-bold text-[#111827] flex-1 truncate">{svc}</span>
                            <select
                                value={current}
                                onChange={(e) => setDuration(svc, parseInt(e.target.value, 10))}
                                className="text-xs font-mono-num border border-gray-300 rounded-sm h-7 px-1 bg-white"
                                data-testid={`workshop-service-duration-select-${svc}`}
                            >
                                {DURATION_OPTIONS.map((d) => (
                                    <option key={d.v} value={d.v}>{d.l}</option>
                                ))}
                            </select>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export function WorkshopScheduleEditor({ value, advanceDays, onChange, onAdvanceChange, capacity, onCapacityChange, durations, onDurationsChange }) {
    const cfg = value || {};
    const slotsFor = (key) => {
        if (key in cfg) return cfg[key] || [];
        return ["mon", "tue", "wed", "thu", "fri"].includes(key) ? DEFAULT_SLOTS : [];
    };
    const setSlots = (key, slots) => onChange({ ...(cfg || {}), [key]: slots });
    const toggleSlot = (key, slot) => {
        const cur = slotsFor(key);
        const next = cur.includes(slot) ? cur.filter((s) => s !== slot) : [...cur, slot].sort();
        setSlots(key, next);
    };
    const resetToDefault = () => onChange(null);

    return (
        <div className="ml-7 mt-3 border-l-2 border-[#FFCC00] pl-3 py-2 bg-[#FFCC00]/5" data-testid="workshop-schedule-editor">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-[#111827]">Öffnungszeiten der Werkstatt</span>
                <button
                    type="button"
                    onClick={resetToDefault}
                    className="text-[10px] font-bold uppercase tracking-wider text-gray-500 hover:text-[#111827] underline"
                    data-testid="workshop-schedule-reset"
                >
                    Standardzeiten verwenden
                </button>
            </div>
            <div className="mb-3 grid grid-cols-2 gap-3">
                <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Buchungsfenster (Tage im Voraus)</Label>
                    <Input
                        type="number"
                        min="1"
                        max="180"
                        value={advanceDays || 14}
                        onChange={(e) => onAdvanceChange(Math.max(1, Math.min(180, parseInt(e.target.value, 10) || 14)))}
                        className="rounded-sm h-8 mt-1 font-mono-num text-sm w-24"
                        data-testid="workshop-advance-days-input"
                    />
                </div>
                <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Kapazität (Hebebühnen / parallel)</Label>
                    <Input
                        type="number"
                        min="1"
                        max="10"
                        value={capacity || 1}
                        onChange={(e) => onCapacityChange(Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 1)))}
                        className="rounded-sm h-8 mt-1 font-mono-num text-sm w-24"
                        data-testid="workshop-capacity-input"
                    />
                    <p className="text-[10px] text-gray-500 mt-1 leading-tight">Wie viele Buchungen pro Slot gleichzeitig möglich sind.</p>
                </div>
            </div>
            <div className="space-y-2">
                {WEEKDAYS.map((w) => {
                    const enabled = slotsFor(w.key).length > 0;
                    return (
                        <div key={w.key} className="bg-white border border-gray-200 rounded-sm p-2" data-testid={`workshop-schedule-${w.key}`}>
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-bold text-[#111827] w-24">{w.label}</span>
                                <label className="inline-flex items-center gap-1 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={enabled}
                                        onChange={() => setSlots(w.key, enabled ? [] : DEFAULT_SLOTS)}
                                        className="sr-only peer"
                                        data-testid={`workshop-schedule-${w.key}-toggle`}
                                    />
                                    <span className="relative w-8 h-4 rounded-full bg-gray-300 peer-checked:bg-[#111827] transition-colors before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:w-3 before:h-3 before:bg-white before:rounded-full before:transition-transform peer-checked:before:translate-x-4"></span>
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{enabled ? "Offen" : "Geschlossen"}</span>
                                </label>
                            </div>
                            {enabled && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                    {TIME_OPTIONS.map((t) => (
                                        <button
                                            key={t}
                                            type="button"
                                            onClick={() => toggleSlot(w.key, t)}
                                            className={`px-2 py-0.5 rounded-sm text-[11px] font-mono-num font-bold border ${slotsFor(w.key).includes(t) ? "bg-[#FFCC00] border-[#111827] text-[#111827]" : "bg-white border-gray-200 text-gray-500 hover:border-[#111827]"}`}
                                            data-testid={`workshop-schedule-${w.key}-slot-${t.replace(":", "")}`}
                                        >
                                            {t}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <p className="text-[10px] text-gray-500 mt-2 leading-tight">
                Klicken Sie auf die Zeit-Pills um Slots an-/abzuwählen. Leer-Auswahl + Standardzeiten wiederherstellen setzt auf Mo-Fr 09-16h zurück.
            </p>
            {/* Jun-2026 (User-Wunsch) — Service-spezifische Termin-Dauer wurde mit den
                „Auftrags-Positionen" zusammengeführt. Die Dauer pro Position (Min.)
                steuert nun direkt die Slot-Länge im Kalender. */}
            <p className="text-[11px] text-gray-500 mt-3 border-t border-gray-200 pt-3 leading-tight" data-testid="service-durations-merged-hint">
                <strong>Termin-Dauer pro Service</strong> pflegen Sie jetzt zusammen mit Preis &amp; MwSt. oben unter <strong>„Auftrags-Positionen (Dauer &amp; Preis)"</strong>. Die dort gesetzte Dauer (Min.) bestimmt automatisch die Slot-Länge.
            </p>
        </div>
    );
}

// Phase 3.36 — Pickup-Partner-Liste (gefiltert nach dealer-PLZ, falls vorhanden).
// Phase 3.43 — Händler kann eigene Dienstleister erfassen + Link zur Self-Registration.
export function PickupPartnerSuggestions() {
    const [items, setItems] = useState(null);
    const [region, setRegion] = useState("");
    const [branches, setBranches] = useState([]);
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ name: "", branch: "", contact_phone: "", contact_email: "", website: "", regions: "" });
    const [busy, setBusy] = useState(false);
    const load = () => {
        // Jun-2026 (User-Wunsch): Suche per PLZ-Präfix ODER Name. Beginnt die
        // Eingabe mit einer Ziffer → als PLZ-Präfix (region) behandeln, sonst
        // als Namenssuche (q).
        const term = region.trim();
        let qs = "";
        if (term) {
            qs = /^\d/.test(term)
                ? `?region=${encodeURIComponent(term)}`
                : `?q=${encodeURIComponent(term)}`;
        }
        api.get(`/pickup-partners${qs}`)
            .then((r) => setItems(r.data?.items || []))
            .catch(() => setItems([]));
    };
    useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [region]);
    useEffect(() => {
        api.get("/service-providers/branches")
            .then((r) => setBranches(r.data?.items || []))
            .catch(() => setBranches([]));
    }, []);

    const submitOwn = async () => {
        if (!form.name.trim()) return;
        setBusy(true);
        try {
            const payload = {
                ...form,
                regions: form.regions.split(",").map((s) => s.trim()).filter(Boolean),
                branch: form.branch || null,
            };
            await api.post("/dealers/me/service-providers", payload);
            setForm({ name: "", branch: "", contact_phone: "", contact_email: "", website: "", regions: "" });
            setAdding(false);
            load();
        } catch { /* toast handled by axios? */ }
        finally { setBusy(false); }
    };

    const deleteOwn = async (id) => {
        if (!window.confirm("Eigenen Dienstleister entfernen?")) return;
        try {
            await api.delete(`/dealers/me/service-providers/${id}`);
            load();
        } catch { /* ignore */ }
    };

    if (items === null) return null;
    // Jul-2026 (User-Wunsch): REGISTRIERTE Dienstleister (mit Account) zuerst,
    // dann System-/eigene Einträge; innerhalb der Gruppen: Geprüfte zuerst,
    // dann günstigster Preis.
    const ranked = [...items].sort((a, b) => {
        const ar = a.is_self_registered ? 0 : 1;
        const br = b.is_self_registered ? 0 : 1;
        if (ar !== br) return ar - br;
        const av = a.verified ? 0 : 1;
        const bv = b.verified ? 0 : 1;
        if (av !== bv) return av - bv;
        const ap = a.price_from_eur == null ? Infinity : a.price_from_eur;
        const bp = b.price_from_eur == null ? Infinity : b.price_from_eur;
        return ap - bp;
    });
    const shown = ranked.slice(0, 5);
    const moreCount = ranked.length - shown.length;
    return (
        <div className="ml-7 mt-2 border-l-2 border-[#FFCC00] pl-3 py-2 bg-[#FFCC00]/5" data-testid="pickup-partner-suggestions">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Truck className="w-4 h-4 text-[#111827]" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#111827] flex-1">Empfohlene Dienstleister</span>
                <button
                    type="button"
                    onClick={() => setAdding((v) => !v)}
                    className="text-[10px] font-bold uppercase tracking-wider bg-[#111827] text-white px-2 py-1 rounded-sm hover:bg-black"
                    data-testid="pickup-partner-add-own-toggle"
                >
                    {adding ? "Abbrechen" : "+ Eigenen Dienstleister erfassen"}
                </button>
            </div>
            <Input
                placeholder="Suche: PLZ (z.B. 10, 80) oder Name…"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="rounded-sm h-8 text-xs mb-2 max-w-[240px]"
                data-testid="pickup-partner-region-input"
            />
            {adding && (
                <div className="mb-3 bg-white border border-gray-300 rounded-sm p-3 space-y-2" data-testid="pickup-partner-own-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Input placeholder="Firmenname *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm text-xs h-8" data-testid="own-sp-name" />
                        <select
                            value={form.branch}
                            onChange={(e) => setForm({ ...form, branch: e.target.value })}
                            className="rounded-sm text-xs h-8 border border-gray-300 px-2 bg-white"
                            data-testid="own-sp-branch"
                        >
                            <option value="">Branche (optional)</option>
                            {branches.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
                        </select>
                        <Input placeholder="Telefon" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="rounded-sm text-xs h-8 font-mono-num" data-testid="own-sp-phone" />
                        <Input placeholder="E-Mail" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="rounded-sm text-xs h-8" data-testid="own-sp-email" />
                        <Input placeholder="Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="rounded-sm text-xs h-8 font-mono-num sm:col-span-2" data-testid="own-sp-website" />
                        <Input placeholder="PLZ-Präfixe kommasepariert" value={form.regions} onChange={(e) => setForm({ ...form, regions: e.target.value })} className="rounded-sm text-xs h-8 font-mono-num sm:col-span-2" data-testid="own-sp-regions" />
                    </div>
                    <button
                        onClick={submitOwn}
                        disabled={busy || !form.name.trim()}
                        className="text-[10px] font-bold uppercase tracking-wider bg-[#FFCC00] text-[#111827] hover:bg-[#E6B800] px-3 py-1.5 rounded-sm disabled:opacity-50"
                        data-testid="own-sp-submit"
                    >
                        Dienstleister speichern
                    </button>
                </div>
            )}
            <div className="mb-2 text-[10px] text-gray-600">
                <a href="/register/service-provider" target="_blank" rel="noreferrer" className="underline font-bold" data-testid="sp-self-register-link">
                    Sie sind selbst Dienstleister? Jetzt registrieren →
                </a>
            </div>
            {items.length === 0 ? (
                <div className="text-xs text-gray-500 py-1">
                    {region ? `Keine Dienstleister für „${region}".` : "Keine Dienstleister gelistet."}
                </div>
            ) : (
                <div className="space-y-2">
                    {shown.map((p) => (
                        <div key={p.id} className="bg-white border border-gray-200 rounded-sm p-2 text-xs" data-testid={`pickup-partner-${p.id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="font-bold text-[#111827]">{p.name}</span>
                                        {p.owner_dealer_id && <span className="text-[9px] uppercase tracking-wider bg-[#FFCC00] text-[#111827] px-1 py-0.5 rounded-sm font-bold">Eigen</span>}
                                        {p.is_self_registered && <span className="text-[9px] uppercase tracking-wider bg-blue-100 text-blue-800 px-1 py-0.5 rounded-sm font-bold">Self-Registered</span>}
                                        {p.verified && <span className="text-[9px] uppercase tracking-wider bg-green-100 text-green-800 px-1 py-0.5 rounded-sm font-bold inline-flex items-center gap-0.5" data-testid={`verified-badge-${p.id}`}><CheckCircle2 className="w-2.5 h-2.5" />Geprüft</span>}
                                        {p.transaction_code && (
                                            <button
                                                type="button"
                                                title="Transaktionscode kopieren — wird bei der Rechnungsstellung (§1 ZweiradhdlVO) anerkannt"
                                                onClick={() => { navigator.clipboard?.writeText(p.transaction_code); toast.success(`Transaktionscode ${p.transaction_code} kopiert`); }}
                                                className="text-[9px] uppercase tracking-wider bg-[#111827] text-[#FFCC00] px-1.5 py-0.5 rounded-sm font-bold font-mono-num hover:bg-black"
                                                data-testid={`transaction-code-${p.id}`}
                                            >
                                                Code: {p.transaction_code}
                                            </button>
                                        )}
                                    </div>
                                    {p.description && <div className="text-gray-600 mt-0.5">{p.description}</div>}
                                    <div className="flex flex-wrap gap-3 mt-1 text-gray-700">
                                        {p.contact_phone && (
                                            <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{p.contact_phone}</span>
                                        )}
                                        {p.contact_email && (
                                            <a href={`mailto:${p.contact_email}`} className="inline-flex items-center gap-1 hover:underline">
                                                <Mail className="w-3 h-3" />{p.contact_email}
                                            </a>
                                        )}
                                        {p.website && (
                                            <a href={p.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline text-[#111827] font-bold">
                                                <ExternalLink className="w-3 h-3" />Website
                                            </a>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {(p.regions || []).slice(0, 6).map((r) => (
                                            <span key={r} className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded-sm text-[10px] font-mono-num">{r}</span>
                                        ))}
                                    </div>
                                </div>
                                <div className="shrink-0 flex flex-col items-end gap-1">
                                    {p.price_from_eur != null && (
                                        <div className="text-right">
                                            <div className="text-[10px] text-gray-500">ab</div>
                                            <div className="font-bold font-mono-num text-[#111827]">{p.price_from_eur.toFixed(2)} €</div>
                                        </div>
                                    )}
                                    {p.owner_dealer_id && (
                                        <button onClick={() => deleteOwn(p.id)} className="text-[10px] text-red-700 hover:underline" data-testid={`delete-own-${p.id}`}>
                                            entfernen
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                    {moreCount > 0 && (
                        <div className="text-[10px] text-gray-500 pt-1" data-testid="pickup-partner-more-hint">
                            + {moreCount} weitere{moreCount === 1 ? "r" : ""} Dienstleister — grenzen Sie über das PLZ-Präfix ein.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
